<script setup lang="ts">
import { useRoute, type RouteLocationNamedRaw } from 'vue-router';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';

export interface SidebarMenuLeaf {
  label: string;
  icon: IconDefinition;
  route: RouteLocationNamedRaw;
}

export type SidebarMenuChildLeaf = Omit<SidebarMenuLeaf, 'icon'>;

export interface SidebarMenuGroup extends Omit<SidebarMenuLeaf, 'route'> {
  children: SidebarMenuChildLeaf[];
  expanded?: boolean;
}

export type SidebarMenuItem = SidebarMenuLeaf | SidebarMenuGroup;

const props = defineProps<{
  menuItems: SidebarMenuItem[];
  mini: boolean;
}>();

const currentRoute = useRoute();
const { t } = useI18n();

// Each item is copied so toggling `expanded` stays local to this instance instead of mutating the caller's menu definition.
const items = ref<SidebarMenuItem[]>(props.menuItems.map(menuItem => ({ ...menuItem })));

// expand sidebar items based on the current route
watch(currentRoute, () => {
  if (!props.mini) {
    items.value.forEach(menuItem => {
      if ('children' in menuItem) {
        menuItem.expanded = menuItem.children.some(child => child.route.name === currentRoute.name);
      }
    });
  }
}, { immediate: true });

// collapse all items when mini mode is active
watch(() => props.mini, mini => {
  if (mini) {
    items.value.forEach(menuItem => {
      if ('children' in menuItem) {
        menuItem.expanded = false;
      }
    });
  }
});

function toggleMenuItemGroup(menuItem: SidebarMenuGroup) {
  menuItem.expanded = !menuItem.expanded;

  if (props.mini && menuItem.expanded) {
    items.value.forEach(item => {
      if (item !== menuItem && 'children' in item) {
        item.expanded = false; // collapse other groups
      }
    });
  }
}
</script>

<template>
  <div class="layout-sidebar" :class="{ 'layout-sidebar-mini': mini }">
    <div class="layout-sidebar-nav" data-test="nav-menu">
      <template v-for="menuItem in items" :key="menuItem.label">
        <div v-if="'children' in menuItem" class="relative">
          <div
            v-tooltip="{ value: menuItem.label, disabled: !mini }"
            class="layout-sidebar-item flex items-center"
            data-test="nav-menu-group"
            @click="toggleMenuItemGroup(menuItem)"
          >
            <fa-icon :class="{ 'mr-4': !mini}" :icon="menuItem.icon" size="lg" />
            <span v-show="!mini">{{ menuItem.label }}</span>
            <span class="flex-1" />
            <fa-icon v-show="!mini" :icon="fasAngleUp" :rotation="menuItem.expanded ? undefined : 180" />
          </div>

          <transition name="sidebar-children" :css="!mini">
            <div v-if="menuItem.expanded" class="layout-sidebar-children min-w-56" data-test="nav-menu-children">
              <div class="layout-sidebar-children-inner flex flex-col gap-[0.4rem] pt-2">
                <router-link
                  v-for="childItem in menuItem.children"
                  :key="childItem.label"
                  :to="childItem.route"
                  class="layout-sidebar-item"
                  data-test="nav-menu-child"
                  @click="mini ? (menuItem.expanded = false) : undefined"
                >
                  <span :class="{ 'ml-12': !mini }">{{ childItem.label }}</span>
                </router-link>
              </div>
            </div>
          </transition>
        </div>

        <router-link
          v-else
          v-tooltip="{ value: menuItem.label, disabled: !mini }"
          class="layout-sidebar-item"
          data-test="nav-menu-item"
          :to="menuItem.route"
        >
          <fa-icon :class="{ 'mr-4': !mini}" :icon="menuItem.icon" size="lg" />
          <span v-show="!mini">{{ menuItem.label }}</span>
        </router-link>
      </template>
    </div>

    <i-button
      href="https://support.intellivend.de/"
      target="_blank"
      :icon="farCircleQuestion"
      :label="mini ? undefined : t('common.support')"
      class="layout-sidebar-button"
      rounded
      size="large"
      as="a"
    />
  </div>
</template>

<style lang="scss">
.layout-sidebar {
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  gap: 0.5rem;
  width: var(--p-layout-sidebar-width);
  background: var(--p-layout-sidebar-background);
  padding: 0.75rem 0rem;
  transition: all 0.3s ease-in-out;
  z-index: 200;
}

// scrollable nav region: grows to fill the space left by the pinned footer
// button and scrolls internally when the items exceed the available height
.layout-sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  // a vertical scrollbar would otherwise force the other axis to scroll too,
  // clipping items against the fixed sidebar width
  overflow-x: hidden;

  // keep items at their natural height so the region overflows (and scrolls)
  // instead of compressing the items and clipping their content
  > * {
    flex-shrink: 0;
  }
}

// expanded-mode menu groups animate open/closed by collapsing the grid row,
// which scales to any number of children instead of a fixed max-height
.layout-sidebar-children {
  display: grid;
  grid-template-rows: 1fr;
}

.layout-sidebar-children-inner {
  overflow: hidden;
}

.layout-sidebar-mini {
  width: var(--p-layout-sidebar-width);

  // mini-mode child menus fly out horizontally via absolute positioning, which
  // an overflow container would clip; keep this region unclipped so they remain
  // visible (at the cost of internal scrolling in mini mode)
  .layout-sidebar-nav {
    overflow: visible;
  }

  .layout-sidebar-children {
    // the flyout is sized by its content, not animated, so the collapsible grid
    // is not needed here
    display: block;
    position: absolute;
    top: 0;
    left: 100%;
    background-color: var(--p-layout-sidebar-background);
    padding-bottom: 0.5rem;
    border-radius: 0 0.3rem 0.3rem 0;

    .layout-sidebar-item {
      margin: 0 0.5rem;
      padding: 0.6rem 0.75rem;
    }
  }
}

.layout-sidebar-item {
  cursor: pointer;
  margin: 0 0.75rem;
  padding: 1rem;
  color: var(--p-slate-50);
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  opacity: 0.7;
  border-radius: 0.3rem;
  white-space: nowrap;
  overflow: hidden;

  svg.fa-fw {
    min-width: var(--fa-fw-width, 1.25em);
  }
}

.layout-sidebar-item:hover,
.layout-sidebar-item.router-link-active:hover {
  background: var(--p-layout-sidebar-active-background);
  opacity: 1.0;
}

.layout-sidebar-item.router-link-active {
  position: relative;
  background: var(--p-layout-sidebar-active-background);
  font-weight: 500;
  opacity: 1.0;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 0.3rem;
    height: 100%;
    border-radius: 0.3rem 0rem 0rem 0.3rem;
    background-color: var(--p-primary-500);
  }
}

.layout-sidebar-button {
  flex: 0 0 auto;
  margin-left: 1rem;
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  width: max-content;
}

// transition for the children of a sidebar menu group

.sidebar-children-enter-active,
.sidebar-children-leave-active {
  transition: grid-template-rows 0.2s ease-in-out;
}
.sidebar-children-enter-from,
.sidebar-children-leave-to {
  grid-template-rows: 0fr;
}
.sidebar-children-enter-to,
.sidebar-children-leave-from {
  grid-template-rows: 1fr;
}
</style>
